import DOMPurify from "dompurify";

const PURIFY_CONFIG = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "target", "loading"],
  FORBID_TAGS: ["script", "object", "embed", "base", "meta"],
};

/**
 * Server-safe fallback used during SSR / in the Worker runtime where no DOM
 * exists. Strips script/style/iframe-hostile constructs, inline event
 * handlers and javascript: URLs with conservative regexes. The browser pass
 * (full DOMPurify) re-sanitizes on hydration, so this only needs to keep the
 * server render safe and crash-free.
 */
function sanitizeHtmlServer(html: string): string {
  return html
    .replace(/<\s*(script|object|embed|base|meta)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*(script|object|embed|base|meta)\b[^>]*\/?>/gi, "")
    .replace(/\s+on[a-z]+\s*=\s*"[^"]*"/gi, "")
    .replace(/\s+on[a-z]+\s*=\s*'[^']*'/gi, "")
    .replace(/\s+on[a-z]+\s*=\s*[^\s>]+/gi, "")
    .replace(/(href|src|xlink:href)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1=$2#$2');
}

/**
 * Cleans editor-saved rich text before it is rendered on public pages.
 * Keeps normal formatting, links, images, tables and video iframes while
 * removing scripts, inline event handlers and javascript: URLs.
 *
 * SSR-safe: uses full DOMPurify in the browser and a conservative string
 * sanitizer on the server so server rendering never throws (React #419).
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return "";
  const input = String(html);
  if (typeof window === "undefined" || typeof document === "undefined") {
    return sanitizeHtmlServer(input);
  }
  return DOMPurify.sanitize(input, PURIFY_CONFIG);
}
